import { useCallback, useEffect, useRef, useState } from 'react'
import {
  FilterOperator,
  FilterValueType,
  FilterableColumn,
  getAllowedOperators,
  getReadableOperator,
} from 'app/filter.utils'
import { OptionType } from 'types/OptionType'

export type FilterType = {
  attribute?: string
  secondaryAttribute?: string
  displayName?: string
  operator?: FilterOperator
  displayOperator?: string
  value?: string | string[]
  secondaryValue?: string[]
  customSearchParam?: string | string[]
  nonEmpty?: boolean
  group?: string
}

export const emptyFilter: FilterType = { attribute: ``, operator: undefined, value: `` }

export type FiltersContainerProps = {
  columns?: FilterableColumn[]
  appliedFilters?: FilterType[]
  defaultJoin?: `and` | `or`
  useOrOption?: boolean
  /** Hide the "where" prefix on the first filter row (useful for single-filter UIs). */
  hideWherePrefix?: boolean
  onFilterChange: (filters: FilterType[]) => void
  onJoinChange: (join: `and` | `or`) => void
  onRemove?: (filters: FilterType[]) => void
}

export function useFiltersContainer({
  columns,
  appliedFilters,
  onFilterChange,
  onJoinChange,
  onRemove,
  defaultJoin,
}: FiltersContainerProps) {
  // No rows until the user adds one: the view renders an empty state in place
  // of a blank placeholder row.
  const [selectedFilters, setSelectedFilters] = useState<FilterType[] | undefined>(
    isPlaceholderOnly(appliedFilters) ? [] : appliedFilters,
  )
  const [join, setJoin] = useState<`and` | `or`>(defaultJoin ?? `and`)

  const appliedKey = JSON.stringify(appliedFilters ?? [])
  const lastAppliedKey = useRef(appliedKey)
  useEffect(() => {
    if (appliedKey === lastAppliedKey.current) return
    lastAppliedKey.current = appliedKey
    setSelectedFilters(isPlaceholderOnly(appliedFilters) ? [] : appliedFilters)
  }, [appliedKey, appliedFilters])

  const handleJoinChange = useCallback(
    (value: `and` | `or`) => {
      setJoin(value)
      onJoinChange(value)
    },
    [onJoinChange],
  )

  const handleChange = useCallback(
    (filters: FilterType[]) => {
      setSelectedFilters(filters)
      onFilterChange(filters)
    },
    [onFilterChange],
  )

  const removeFilter = useCallback(
    (index: number) => {
      let updatedFilters = [...(selectedFilters ?? [])]
      updatedFilters?.splice(index, 1)
      setSelectedFilters(updatedFilters)
      if (onRemove) onRemove(updatedFilters)
      else onFilterChange(updatedFilters)
    },
    [selectedFilters, onFilterChange, onRemove],
  )

  const addFilter = useCallback(() => {
    setSelectedFilters((prev) => [...(prev ?? []), emptyFilter])
  }, [])

  const handleSelectorChange = useCallback(
    (index: number, columnSelected: OptionType) => {
      const column = columns?.find((c) => c.attribute === columnSelected.id)
      if (!column) return
      const updatedTerms = [...(selectedFilters ?? [])]
      updatedTerms.splice(index, 1, {
        attribute: columnSelected.id,
        displayName: columnSelected.name,
        operator: column.defaultOperator ?? getAllowedOperators(column.type!)[0] ?? undefined,
        displayOperator: getReadableOperator(
          column.defaultOperator ?? column.operator ?? getAllowedOperators(column.type!)[0],
          column.type!,
        ),
        value: getDefaultValue(column),
        customSearchParam: column.customSearchParam,
        nonEmpty: column.nonEmpty,
      })
      handleChange(updatedTerms)
    },
    [columns, selectedFilters, handleChange],
  )

  const handleOperationChange = useCallback(
    (index: number, option: OptionType) => {
      const updatedTerms = [...(selectedFilters ?? [])]
      updatedTerms.splice(index, 1, {
        ...selectedFilters?.[index],
        operator: option.id as FilterOperator,
        displayOperator: option.name,
      })
      handleChange(updatedTerms)
    },
    [selectedFilters, handleChange],
  )

  return {
    selectedFilters,
    join,
    handleJoinChange,
    handleChange,
    removeFilter,
    addFilter,
    handleSelectorChange,
    handleOperationChange,
  }
}

/** True when the applied set is empty or holds only the blank placeholder row that TableFilters seeds. */
function isPlaceholderOnly(filters?: FilterType[]): boolean {
  return !filters?.length || (filters.length === 1 && filters[0].attribute === emptyFilter.attribute)
}

function getDefaultValue(column: FilterableColumn) {
  const type = column?.type
  if (`defaultValue` in column && !column.defaultFilter) return column?.defaultValue
  if (type === FilterValueType.string) return ``
  if (type === FilterValueType.stringArray || type === FilterValueType.id || type === FilterValueType.multiple)
    return []
  return undefined
}
